import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { SalesLayout } from '../components/crm/SalesLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, Rocket, TrendingUp, Trophy, Percent, Building2 } from 'lucide-react';
import { crmFetch } from '../components/crm/crmApi';
import { DealModal } from '../components/crm/DealModal';
import { DEAL_STAGES, dealStageMeta, dealPlanLabel, fmtMoney, fmtDate } from '../components/crm/constants';

const KPI = ({ icon: Icon, label, value, tone, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${tone}`} />
        <p className="text-[11px] text-gray-500 uppercase tracking-wide">{label}</p>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </CardContent>
  </Card>
);

export default function SalesPipeline() {
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'jobasti_admin';
  const [deals, setDeals] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tableMissing, setTableMissing] = useState(false);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState({ open: false, deal: null, defaultStage: null });

  const load = useCallback(async () => {
    const res = await crmFetch('/deals');
    if (res.tableMissing) setTableMissing(true);
    else if (res.success) { setDeals(res.data || []); setSnapshot(res.snapshot || null); setTableMissing(false); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const moveStage = async (deal, stage) => {
    if (stage === deal.stage) return;
    const res = await crmFetch(`/deals/${deal.id}`, { method: 'PATCH', body: JSON.stringify({ stage }) });
    if (res.success) { toast.success(`Moved to ${dealStageMeta(stage).label}`); load(); }
    else toast.error(res.message || 'Failed to move deal');
  };

  const deleteDeal = async (deal) => {
    if (!window.confirm(`Delete the deal "${deal.name}"?`)) return;
    const res = await crmFetch(`/deals/${deal.id}`, { method: 'DELETE' });
    if (res.success) { toast.success('Deal deleted'); load(); }
  };

  const q = search.trim().toLowerCase();
  const filtered = deals.filter((d) => {
    if (!q) return true;
    return [d.name, d.company_name, d.contact_name].some((v) => (v || '').toLowerCase().includes(q));
  });

  return (
    <SalesLayout>
      <div data-testid="software-pipeline-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Rocket className="w-6 h-6 text-blue-600" /> Software Pipeline
            </h1>
            <p className="text-sm text-gray-500 mt-1">Selling the JobastiIQ platform — separate from recruitment accounts.</p>
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setModal({ open: true, deal: null, defaultStage: null })} data-testid="add-deal-btn">
            <Plus className="w-4 h-4 mr-2" /> New Deal
          </Button>
        </div>

        {tableMissing && (
          <Card className="border-amber-300 bg-amber-50" data-testid="crm-migration-notice">
            <CardContent className="p-6 text-sm text-amber-800">
              Software pipeline tables not migrated yet — run <code className="px-1 bg-amber-100 rounded">2026-06-17-crm-phase6.sql</code> in the Supabase SQL Editor, then refresh.
            </CardContent>
          </Card>
        )}

        {!tableMissing && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <KPI icon={TrendingUp} label="Open Pipeline (ARR)" tone="text-blue-500" value={loading ? '…' : (fmtMoney(snapshot?.openArr ?? 0) || '€0')} testId="pipeline-kpi-open" />
              <KPI icon={Percent} label="Weighted (ARR)" tone="text-violet-500" value={loading ? '…' : (fmtMoney(snapshot?.weightedArr ?? 0) || '€0')} testId="pipeline-kpi-weighted" />
              <KPI icon={Trophy} label="Won ARR (YTD)" tone="text-emerald-500" value={loading ? '…' : (fmtMoney(snapshot?.wonArrYtd ?? 0) || '€0')} testId="pipeline-kpi-won" />
              <KPI icon={Rocket} label="Win Rate" tone="text-amber-500" value={loading ? '…' : `${snapshot?.winRate ?? 0}%`} testId="pipeline-kpi-winrate" />
            </div>

            <div className="relative flex-1 max-w-sm mb-4">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input className="pl-9" placeholder="Search deals…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="pipeline-search-input" />
            </div>

            {loading ? (
              <p className="text-center text-gray-400 py-16">Loading pipeline…</p>
            ) : (
              <div className="flex gap-3 overflow-x-auto pb-4" data-testid="pipeline-board">
                {DEAL_STAGES.map((stage) => {
                  const stageDeals = filtered.filter((d) => d.stage === stage.value);
                  const stageArr = stageDeals.reduce((sum, d) => sum + (Number(d.arr) || 0), 0);
                  return (
                    <div key={stage.value} className="flex-shrink-0 w-72" data-testid={`pipeline-col-${stage.value}`}>
                      <div className={`rounded-t-xl border-t-4 ${stage.col} bg-white border-x border-gray-200 px-3 py-2.5 flex items-center justify-between`}>
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${stage.dot}`} />
                          <span className="text-sm font-semibold text-gray-800">{stage.label}</span>
                          <span className="text-xs text-gray-400">{stageDeals.length}</span>
                        </div>
                        <span className="text-[11px] font-medium text-gray-500">{fmtMoney(stageArr) || '€0'}</span>
                      </div>
                      <div className="bg-gray-50/70 border-x border-b border-gray-200 rounded-b-xl p-2 space-y-2 min-h-[120px]">
                        {stageDeals.length === 0 && <p className="text-xs text-gray-400 text-center py-6">No deals</p>}
                        {stageDeals.map((d) => (
                          <div key={d.id} className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm hover:shadow-md transition-shadow" data-testid={`deal-card-${d.id}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-900 truncate">{d.name}</p>
                                <p className="text-xs text-gray-500 flex items-center gap-1 truncate">
                                  <Building2 className="w-3 h-3 shrink-0" /> {d.company_name}
                                </p>
                              </div>
                              <div className="flex gap-0.5 shrink-0">
                                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setModal({ open: true, deal: d, defaultStage: null })} data-testid={`edit-deal-${d.id}`}>
                                  <Pencil className="w-3 h-3" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-6 w-6 text-rose-500" onClick={() => deleteDeal(d)} data-testid={`delete-deal-${d.id}`}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-bold text-gray-900">{fmtMoney(d.arr, d.currency) || '€0'}<span className="text-[10px] font-normal text-gray-400"> ARR</span></span>
                              {dealPlanLabel(d.plan) && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{dealPlanLabel(d.plan)}</span>}
                            </div>
                            <div className="mt-1 flex items-center justify-between text-[11px] text-gray-400">
                              <span>{d.expected_close_date ? `Close ${fmtDate(d.expected_close_date)}` : '—'}</span>
                              {isAdmin && d.ownerName && <span className="truncate max-w-[90px]">{d.ownerName}</span>}
                            </div>
                            <Select value={d.stage} onValueChange={(v) => moveStage(d, v)}>
                              <SelectTrigger className="h-7 mt-2 text-xs" data-testid={`deal-stage-move-${d.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {DEAL_STAGES.map((s) => <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      <DealModal
        open={modal.open}
        onOpenChange={(o) => setModal((s) => ({ ...s, open: o }))}
        onSaved={load}
        deal={modal.deal}
        defaultStage={modal.defaultStage}
      />
    </SalesLayout>
  );
}
